import { z } from "zod";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import NameSchema from "_shared/names/name-schema";
import EmailAddressSchema from "_shared/email-addresses/email-address-schema";
import PasswordSchema from "users/auth/credentials/passwords/password-schema";
import EmailAddress from "_shared/email-addresses/email-address";
import Name from "_shared/names/name";
import Password from "users/auth/credentials/passwords/password";

export type RegistrationInfoJSON = z.infer<typeof RegistrationInfoSchema.RegistrationInfoJSONZ>;
export type RegistrationInfoParams = z.infer<typeof RegistrationInfoSchema.RegistrationInfoZ>;

export default class RegistrationInfoSchema {
  public static readonly RegistrationInfoJSONZ = z.object({
    name: NameSchema.NameJSONZ,
    email: EmailAddressSchema.EmailAddressJSONZ,
    password: PasswordSchema.PasswordJSONZ.nullish(),
  });

  public static readonly RegistrationInfoZ = z.object({
    name: z.instanceof(Name),
    email: z.instanceof(EmailAddress),
    password: z.instanceof(Password).optional(),
  });

  public static readonly codec = z.codec(
    RegistrationInfoSchema.RegistrationInfoJSONZ,
    RegistrationInfoSchema.RegistrationInfoZ,
    {
      decode: (data) => ({
        name: new Name(NameSchema.deserialize(data.name)),
        email: new EmailAddress(EmailAddressSchema.deserialize(data.email)),
        password: Password.fromJSON(data.password) ?? undefined,
      }),
      encode: (registrationInfo) => ({
        name: registrationInfo.name.toJSON(),
        email: registrationInfo.email.toJSON(),
        password: registrationInfo.password?.toJSON(),
      }),
    }
  );

  public static validate(data: unknown, logError: boolean = true): RegistrationInfoParams {
    return parseOrThrow(RegistrationInfoSchema.RegistrationInfoZ, data, {
      logError,
      schemaName: "RegistrationInfoSchema",
      operation: "validate",
      payloadSample: data,
    });
  }

  public static validateJSON(data: unknown): RegistrationInfoJSON {
    return parseOrThrow(RegistrationInfoSchema.RegistrationInfoJSONZ, data, {
      logError: true,
      schemaName: "RegistrationInfoSchema",
      operation: "validateJSON",
      payloadSample: data,
    });
  }

  public static isValid(data: unknown): data is RegistrationInfoParams {
    try {
      RegistrationInfoSchema.validate(data);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(data: unknown): data is RegistrationInfoParams {
    try {
      RegistrationInfoSchema.validateJSON(data);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: unknown): RegistrationInfoParams {
    const parsed = parseOrThrow(RegistrationInfoSchema.RegistrationInfoJSONZ, data, {
      logError: true,
      schemaName: "RegistrationInfoSchema",
      operation: "deserialize",
      payloadSample: data,
    });
    return RegistrationInfoSchema.codec.decode(parsed);
  }

  public static serialize(data: RegistrationInfoParams): RegistrationInfoJSON {
    const parsed = RegistrationInfoSchema.validate(data);
    return RegistrationInfoSchema.codec.encode(parsed);
  }
}
